/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';

type ApiResult<T = any> = {
  success: boolean;
  result: T;
};

type DatabaseId = string | number;

type ScreeningInitiator = {
  id?: string;
  name?: string;
};

type ScreeningInitiatorInput = ScreeningInitiator | string;

export type ProductDatabaseScreeningRequest =
  | DatabaseId
  | DatabaseId[]
  | {
      databaseId?: DatabaseId;
      databaseIds?: DatabaseId | DatabaseId[];
      skipEmail?: boolean;
      initiatedBy?: ScreeningInitiatorInput;
      [key: string]: any;
    };

export type SingleProductScreeningRequest = {
  databaseId: DatabaseId;
  databaseProductId?: DatabaseId;
  productId?: DatabaseId;
  skipEmail?: boolean;
  initiatedBy?: ScreeningInitiatorInput;
  [key: string]: any;
};

// QA/prod set REACT_APP_PRODUCT_URL to an absolute host. Locally it's unset, so
// we fall back to the nginx path proxy at `/product-service` (see
// nginx/nginx.conf) -- same-origin, which keeps the initAxios auth interceptor
// working and avoids a CORS preflight. Same shape as ENTITY_SERVICE_BASE_URL.
// The fallback MUST be a non-empty string: initAxios treats a falsy override as
// "unset" and silently falls back to REACT_APP_API_URL, which sends every
// product call to bite-server instead of failing loudly.
const getProductServiceBaseUrl = () => {
  // eslint-disable-next-line no-undef
  return process.env.REACT_APP_PRODUCT_URL || '/product-service';
};

const errorResult = (label: string, err: unknown): ApiResult => {
  if ((err as { code?: string } | null)?.code !== 'ERR_CANCELED') {
    console.error(`ProductServiceAPI.${label} failed`, err);
  }
  return { success: false, result: parseErrorMsg(err) };
};

// Reuse one client so a dashboard request burst does not repeatedly allocate
// Axios instances and identical auth interceptors. The base URL is build-time
// configuration, so it cannot change during a browser session.
const productServiceClient = initAxios(getProductServiceBaseUrl());

const getProductServiceClient = async () => productServiceClient;

const normalizeInitiatedBy = (initiatedBy?: ScreeningInitiatorInput) => {
  if (typeof initiatedBy === 'string') {
    return { id: initiatedBy };
  }

  return initiatedBy;
};

const normalizeDatabaseScreeningRequest = (
  request: ProductDatabaseScreeningRequest,
) => {
  if (typeof request === 'string' || typeof request === 'number') {
    return { databaseIds: String(request) };
  }

  if (Array.isArray(request)) {
    return { databaseIds: request.map((id) => String(id)) };
  }

  const { databaseId, databaseIds, ...rest } = request;
  const ids = Array.isArray(databaseIds)
    ? databaseIds.map((id) => String(id))
    : databaseIds !== undefined
    ? String(databaseIds)
    : databaseId !== undefined
    ? String(databaseId)
    : undefined;

  return {
    databaseIds: ids,
    ...rest,
    initiatedBy: normalizeInitiatedBy(rest.initiatedBy),
  };
};

const normalizeSingleProductScreeningRequest = (
  request: SingleProductScreeningRequest,
) => {
  if (!request.databaseId || !request.databaseProductId) {
    throw new Error('databaseId and databaseProductId are required');
  }

  return {
    ...request,
    databaseId: String(request.databaseId),
    databaseProductId: String(request.databaseProductId),
    productId:
      request.productId === undefined ? undefined : String(request.productId),
    initiatedBy: normalizeInitiatedBy(request.initiatedBy),
  };
};

const withProductService = async <T,>(
  label: string,
  fn: () => Promise<T>,
): Promise<ApiResult<T>> => {
  try {
    const result = await fn();
    return { success: true, result: result };
  } catch (err) {
    return errorResult(label, err);
  }
};

/**
 * Wrapper for the biteservices product service.
 *
 * Browser calls go through the same-origin proxy at `/product-service`. The
 * `/screenings/run/*` methods call the Product Service HTTP app; that app
 * creates screening jobs and publishes work consumed by `services/product/src/worker.ts`.
 */
export const ProductServiceAPI = {
  // ---------------------------------------------------------------------------
  // App / service health
  // ---------------------------------------------------------------------------

  getServiceInfo: async () =>
    withProductService('getServiceInfo', async () => {
      const resp = await (await getProductServiceClient()).get('');
      return resp.data;
    }),

  getHealth: async () =>
    withProductService('getHealth', async () => {
      const resp = await (await getProductServiceClient()).get('health');
      return resp.data;
    }),

  pulse: async () =>
    withProductService('pulse', async () => {
      const resp = await (await getProductServiceClient()).get('pulse');
      return resp.data;
    }),

  getOpenApiSpec: async () =>
    withProductService('getOpenApiSpec', async () => {
      const resp = await (await getProductServiceClient()).get('docs.json');
      return resp.data;
    }),

  // ---------------------------------------------------------------------------
  // Search routes
  // ---------------------------------------------------------------------------

  searchEntity: async (params: {
    name: string;
    fuzzy?: boolean;
    country?: string;
    size?: number;
  }) =>
    withProductService('searchEntity', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/entity', params);
      return resp.data;
    }),

  searchProduct: async (params: {
    name: string;
    fuzzy?: boolean;
    country?: string;
    size?: number;
  }) =>
    withProductService('searchProduct', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/product', params);
      return resp.data;
    }),

  // Legacy-style alias for callers mirroring DatahubAPI.commoditySearch.
  productSearch: async (params: {
    name: string;
    fuzzy?: boolean;
    country?: string;
    size?: number;
  }) =>
    withProductService('productSearch', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/product', params);
      return resp.data;
    }),

  // Ten-digit USHTS codes currently effective as of today, filtered to a
  // 6-10 digit prefix (and optionally a country). Backs the "find a current
  // replacement" flow for HTS codes createProduct rejects as no longer in
  // the current schedule -- see hts-classification.service.ts's
  // searchCurrentCodes on the product-service side.
  searchHtsCodes: async (params: { prefix: string; country?: string }) =>
    withProductService('searchHtsCodes', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/hts-codes', params);
      return resp.data;
    }),

  // Live commodity/regulatory-list search (OpenSearch-backed). Body is
  // wrapped in `{ scoreParams }` per the controller's contract — unlike
  // searchEntity/searchProduct above, which post their params unwrapped.
  searchCommodity: async (scoreParams: {
    indices: string[];
    primary?: string;
    hsCode?: string[];
    lens?: string[];
    listCountry?: string[];
    listName?: string[];
    tradeType?: string;
    qty?: number;
    agg?: boolean;
    pageNum?: number;
  }) =>
    withProductService('searchCommodity', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/commodity', { scoreParams: scoreParams });
      return resp.data;
    }),

  searchHtsCodes: async (params: { prefix: string; country?: string }) =>
    withProductService('searchHtsCodes', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('search/hts-codes', params);
      return resp.data;
    }),

  // ---------------------------------------------------------------------------
  // Database / product routes
  // ---------------------------------------------------------------------------

  getDatabaseById: async (databaseId: DatabaseId) =>
    withProductService('getDatabaseById', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/${databaseId}`);
      return resp.data;
    }),

  getDatabaseIssueSummary: async (
    databaseId: DatabaseId,
    page = 1,
    pageSize = 10,
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabaseIssueSummary', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/${databaseId}/issues-summary`, {
        params: { page: page, pageSize: pageSize },
        signal: signal,
      });
      return resp.data;
    }),

  getDatabaseNotifications: async (
    databaseId: DatabaseId,
    params: {
      view?: 'all' | 'system' | 'user';
      unreadOnly?: boolean;
      limit?: number;
      offset?: number;
      countsOnly?: boolean;
    } = {},
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabaseNotifications', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/${databaseId}/notifications`, {
        params: params,
        signal: signal,
      });
      return resp.data;
    }),

  getGroupNotifications: async (
    groupId: string,
    params: {
      view?: 'all' | 'system' | 'user';
      unreadOnly?: boolean;
      limit?: number;
      offset?: number;
      countsOnly?: boolean;
    } = {},
    signal?: AbortSignal,
  ) =>
    withProductService('getGroupNotifications', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/group-id/${groupId}/notifications`, {
        params: params,
        signal: signal,
      });
      return resp.data;
    }),

  markDatabaseNotificationRead: async (
    databaseId: DatabaseId,
    notificationId: string,
  ) =>
    withProductService('markDatabaseNotificationRead', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch(`database/${databaseId}/notifications/${notificationId}/read`);
      return resp.data;
    }),

  markAllDatabaseNotificationsRead: async (databaseId: DatabaseId) =>
    withProductService('markAllDatabaseNotificationsRead', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch(`database/${databaseId}/notifications/read-all`);
      return resp.data;
    }),

  markAllGroupNotificationsRead: async (groupId: string) =>
    withProductService('markAllGroupNotificationsRead', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch(`database/group-id/${groupId}/notifications/read-all`);
      return resp.data;
    }),

  dismissDatabaseNotification: async (
    databaseId: DatabaseId,
    notificationId: string,
  ) =>
    withProductService('dismissDatabaseNotification', async () => {
      const resp = await (
        await getProductServiceClient()
      ).delete(`database/${databaseId}/notifications/${notificationId}`);
      return resp.data;
    }),

  getDatabasesByGroupId: async (
    groupId: string,
    type?: string,
    pagination?: {
      page?: number;
      limit?: number;
      view?: 'cards';
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabasesByGroupId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/group-id/${groupId}`, {
        signal: signal,
        params: pagination
          ? { type: type, paginated: true, ...pagination }
          : type
          ? { type: type }
          : undefined,
      });
      return resp.data;
    }),

  getDatabaseListItemsByGroupId: async (groupId: string, type?: string) =>
    withProductService('getDatabaseListItemsByGroupId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/group-id/${groupId}/list-items`, {
        params: type ? { type: type } : undefined,
      });
      return resp.data;
    }),

  createDatabase: async (database: {
    name: string;
    group_id: string;
    created_by: string;
    description?: string;
    icon?: string;
    accent_color?: string;
    subtype?: string;
    database_structure?: string;
    screening_frequency?: string;
    screening_day_of_week?: string;
    notifications_email?: string;
    notifications_enabled?: boolean;
  }) =>
    withProductService('createDatabase', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('database', database);
      return resp.data;
    }),

  bulkDeleteDatabaseProducts: async (
    databaseId: DatabaseId,
    databaseProductIds: string[],
  ) =>
    withProductService('bulkDeleteDatabaseProducts', async () => {
      const resp = await (
        await getProductServiceClient()
      ).delete('product/database-products', {
        data: {
          database_id: String(databaseId),
          database_product_ids: databaseProductIds,
        },
      });
      return resp.data;
    }),

  getBulkTariffExclusionOptions: async (
    databaseId: DatabaseId,
    databaseProductIds: string[],
  ) =>
    withProductService('getBulkTariffExclusionOptions', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product/database-products/tariff-exclusions/options', {
        database_id: String(databaseId),
        database_product_ids: databaseProductIds,
      });
      return resp.data;
    }),

  bulkUpdateTariffExclusion: async (
    databaseId: DatabaseId,
    databaseProductIds: string[],
    exclusionCode: string,
    action: 'apply' | 'remove',
  ) =>
    withProductService('bulkUpdateTariffExclusion', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch('product/database-products/tariff-exclusions', {
        database_id: String(databaseId),
        database_product_ids: databaseProductIds,
        exclusion_code: exclusionCode,
        action: action,
      });
      return resp.data;
    }),

  bulkUpdateDatabaseProductStatus: async (
    databaseId: DatabaseId,
    databaseProductIds: string[],
    statusFlag: string,
  ) =>
    withProductService('bulkUpdateDatabaseProductStatus', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch('product/database-products/status', {
        database_id: String(databaseId),
        database_product_ids: databaseProductIds,
        status_flag: statusFlag,
      });
      return resp.data;
    }),

  updateDatabaseSettings: async (
    databaseId: DatabaseId,
    settings: {
      screening_frequency: string;
      screening_day_of_week?: string | null;
      notifications_email?: string | null;
      notifications_enabled: boolean;
      screening_lists: string[];
    },
  ) =>
    withProductService('updateDatabaseSettings', async () => {
      const resp = await (
        await getProductServiceClient()
      ).put(`database/${databaseId}/settings`, settings);
      return resp.data;
    }),

  getDatabaseSavedViews: async (databaseId: DatabaseId) =>
    withProductService('getDatabaseSavedViews', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/${databaseId}/saved-views`);
      return resp.data;
    }),

  createDatabaseSavedView: async (
    databaseId: DatabaseId,
    view: {
      name: string;
      configuration: Record<string, any>;
    },
  ) =>
    withProductService('createDatabaseSavedView', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`database/${databaseId}/saved-views`, view);
      return resp.data;
    }),

  updateDatabaseSavedView: async (
    databaseId: DatabaseId,
    savedViewId: string,
    update: {
      name?: string;
      configuration?: Record<string, any>;
    },
  ) =>
    withProductService('updateDatabaseSavedView', async () => {
      const resp = await (
        await getProductServiceClient()
      ).put(`database/${databaseId}/saved-views/${savedViewId}`, update);
      return resp.data;
    }),

  deleteDatabaseSavedView: async (
    databaseId: DatabaseId,
    savedViewId: string,
  ) =>
    withProductService('deleteDatabaseSavedView', async () => {
      const resp = await (
        await getProductServiceClient()
      ).delete(`database/${databaseId}/saved-views/${savedViewId}`);
      return resp.data;
    }),

  deleteDatabase: async (databaseId: DatabaseId) =>
    withProductService('deleteDatabase', async () => {
      const resp = await (
        await getProductServiceClient()
      ).delete(`database/${databaseId}`);
      return resp.data;
    }),

  createProduct: async (product: {
    database_id: DatabaseId;
    name: string;
    [key: string]: any;
  }) =>
    withProductService('createProduct', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product', product);
      return resp.data;
    }),

  getProductById: async (productId: DatabaseId) =>
    withProductService('getProductById', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${productId}`);
      return resp.data;
    }),

  getProductsByGroupId: async (
    groupId: string,
    params?: Record<string, string | number | boolean | undefined>,
    signal?: AbortSignal,
  ) =>
    withProductService('getProductsByGroupId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/group-id/${groupId}`, { params: params, signal: signal });
      return resp.data;
    }),

  getProductsSummaryByGroupId: async (groupId: string, signal?: AbortSignal) =>
    withProductService('getProductsSummaryByGroupId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/group-id/${groupId}/summary`, { signal: signal });
      return resp.data;
    }),

  getProductsByDatabaseId: async (
    databaseId: DatabaseId,
    params?: Record<string, string | number | boolean | undefined>,
    signal?: AbortSignal,
  ) =>
    withProductService('getProductsByDatabaseId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/database-id/${databaseId}`, {
        params: params,
        signal: signal,
      });
      return resp.data;
    }),

  getProductsSummaryByDatabaseId: async (
    databaseId: DatabaseId,
    signal?: AbortSignal,
  ) =>
    withProductService('getProductsSummaryByDatabaseId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/database-id/${databaseId}/summary`, { signal: signal });
      return resp.data;
    }),

  getDatabasePgaExposure: async (
    databaseId: DatabaseId,
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabasePgaExposure', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/database-id/${databaseId}/pga-exposure`, {
        signal: signal,
      });
      return resp.data;
    }),

  getDatabaseTariffExposure: async (
    databaseId: DatabaseId,
    includeProducts = false,
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabaseTariffExposure', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/database-id/${databaseId}/tariff-exposure`, {
        params: includeProducts ? { includeProducts: 'true' } : undefined,
        signal: signal,
      });
      return resp.data;
    }),

  // Tariff exposure limited to the given database products (e.g. a CSV
  // export's rows); POST because the id list can be long.
  getDatabaseTariffExposureForProducts: async (
    databaseId: DatabaseId,
    databaseProductIds: string[],
    includeProducts = false,
    signal?: AbortSignal,
  ) =>
    withProductService('getDatabaseTariffExposureForProducts', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(
        `product/database-id/${databaseId}/tariff-exposure`,
        {
          databaseProductIds: databaseProductIds,
          includeProducts: includeProducts,
        },
        { signal: signal },
      );
      return resp.data;
    }),

  getProductsByParentId: async (
    productId: DatabaseId,
    databaseId: DatabaseId,
  ) =>
    withProductService('getProductsByParentId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/parent-id/${productId}`, { params: { databaseId: databaseId } });
      return resp.data;
    }),

  getBomTree: async (databaseId: DatabaseId, productIds: DatabaseId[]) =>
    withProductService('getBomTree', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product/bom-tree', {
        database_id: String(databaseId),
        product_ids: productIds.map((id) => String(id)),
      });
      return resp.data;
    }),

  getProductCategories: async (groupId: string) =>
    withProductService('getProductCategories', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/categories/${groupId}`);
      return resp.data;
    }),

  createProductCategory: async (groupId: string, name: string) =>
    withProductService('createProductCategory', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`product/categories/${groupId}`, { name: name });
      return resp.data;
    }),

  updateProductDetails: async (
    databaseProductId: DatabaseId,
    details: Record<string, unknown>,
  ) =>
    withProductService('updateProductDetails', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch(`product/${databaseProductId}/details`, details);
      return resp.data;
    }),

  getProductScreening: async (databaseProductId: DatabaseId) =>
    withProductService('getProductScreening', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${databaseProductId}/screening`);
      return resp.data;
    }),

  getProductScreeningHistory: async (
    databaseProductId: DatabaseId,
    params: { limit?: number; offset?: number } = {},
  ) =>
    withProductService('getProductScreeningHistory', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${databaseProductId}/screening-history`, {
        params: params,
      });
      return resp.data;
    }),

  getProductTariffHistory: async (
    databaseProductId: DatabaseId,
    params: { limit?: number } = {},
  ) =>
    withProductService('getProductTariffHistory', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${databaseProductId}/tariff-history`, {
        params: params,
      });
      return resp.data;
    }),

  getProductHtsClassification: async (databaseProductId: DatabaseId) =>
    withProductService('getProductHtsClassification', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${databaseProductId}/hts-classification`);
      return resp.data;
    }),

  getProductHtsRecommendations: async (databaseProductId: DatabaseId) =>
    withProductService('getProductHtsRecommendations', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`product/${databaseProductId}/hts-recommendations`);
      return resp.data;
    }),

  updateProductComplianceFlag: async (
    databaseProductId: DatabaseId,
    flag: string,
    cleared: boolean,
  ) =>
    withProductService('updateProductComplianceFlag', async () => {
      const resp = await (
        await getProductServiceClient()
      ).patch(`product/${databaseProductId}/compliance-flags`, {
        flag: flag,
        cleared: cleared,
      });
      return resp.data;
    }),

  previewProductTariff: async (
    databaseProductId: DatabaseId,
    options: {
      enabledExclusions?: Record<string, boolean>;
      enabledFtas?: Record<string, boolean>;
    },
  ) =>
    withProductService('previewProductTariff', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`product/${databaseProductId}/tariff-preview`, options);
      return resp.data;
    }),

  getProductCrossRulings: async (databaseProductId: DatabaseId) =>
    withProductService('getProductCrossRulings', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`product/${databaseProductId}/cross-rulings`);
      return resp.data;
    }),

  getProductPgaFlags: async (databaseProductId: DatabaseId) =>
    withProductService('getProductPgaFlags', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/${databaseProductId}/pga-flags`);
      return resp.data;
    }),

  getPgaFlagDefinitions: async () =>
    withProductService('getPgaFlagDefinitions', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get('product/pga-flag-definitions');
      return resp.data;
    }),

  getPgaFlagDefinition: async (code: string) =>
    withProductService('getPgaFlagDefinition', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/pga-flag-definitions/${code}`);
      return resp.data;
    }),

  downloadPgaFlagTemplate: async (code: string) =>
    withProductService('downloadPgaFlagTemplate', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/pga-flag-definitions/${code}/template`, {
        responseType: 'blob',
      });
      return resp.data as Blob;
    }),

  previewPgaFlagTemplate: async (code: string, csvText: string) =>
    withProductService('previewPgaFlagTemplate', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(`product/pga-flag-definitions/${code}/preview`, {
        csvText: csvText,
      });
      return resp.data;
    }),

  upsertProductPgaFlag: async (
    databaseProductId: DatabaseId,
    code: string,
    body: {
      values: Record<string, unknown>;
      repeaters: Record<string, Record<string, unknown>[]>;
      documents: Array<{
        id: string;
        name: string;
        kind: string;
        size: number;
        uploadedAt: string;
      }>;
    },
  ) =>
    withProductService('upsertProductPgaFlag', async () => {
      const resp = await (
        await getProductServiceClient()
      ).put(`product/${databaseProductId}/pga-flags/${code}`, body);
      return resp.data;
    }),

  deleteProductPgaFlag: async (databaseProductId: DatabaseId, code: string) =>
    withProductService('deleteProductPgaFlag', async () => {
      const resp = await (
        await getProductServiceClient()
      ).delete(`product/${databaseProductId}/pga-flags/${code}`);
      return resp.data;
    }),

  getScreeningRunsByDatabaseId: async (databaseId: DatabaseId) =>
    withProductService('getScreeningRunsByDatabaseId', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`database/${databaseId}/screening-runs`);
      return resp.data;
    }),

  bulkUploadProducts: async (formData: FormData) =>
    withProductService('bulkUploadProducts', async () => {
      const apiClient = await getProductServiceClient();
      const resp = await apiClient.post('product/bulk-file-upload', formData, {
        headers: { 'Content-Type': undefined },
      });
      return resp.data;
    }),

  getProductImportJob: async (jobId: string) =>
    withProductService('getProductImportJob', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/import-jobs/${jobId}`);
      return resp.data;
    }),

  createCargoWiseExportJob: async (input: {
    databaseId: DatabaseId;
    fileName: string;
    productIds?: DatabaseId[];
  }) =>
    withProductService('createCargoWiseExportJob', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product/export/cargowise/jobs', {
        database_id: String(input.databaseId),
        file_name: input.fileName,
        product_ids: input.productIds?.map((id) => String(id)),
      });
      return resp.data;
    }),

  getCargoWiseExportReadiness: async (input: {
    databaseId: DatabaseId;
    productIds?: DatabaseId[];
  }) =>
    withProductService('getCargoWiseExportReadiness', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product/export/cargowise/readiness', {
        database_id: String(input.databaseId),
        product_ids: input.productIds?.map((id) => String(id)),
      });
      return resp.data;
    }),

  createImportPdfReportJob: async (input: {
    databaseId: DatabaseId;
    fileName: string;
    productIds?: DatabaseId[];
    filters?: Record<string, unknown>;
  }) =>
    withProductService('createImportPdfReportJob', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post('product/export/pdf/jobs', {
        database_id: String(input.databaseId),
        file_name: input.fileName,
        product_ids: input.productIds?.map((id) => String(id)),
        filters: input.filters,
      });
      return resp.data;
    }),

  getProductExportJob: async (jobId: string) =>
    withProductService('getProductExportJob', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/export/jobs/${jobId}`);
      return resp.data;
    }),

  downloadImportPdfReport: async (jobId: string) =>
    withProductService('downloadImportPdfReport', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get(`product/export/jobs/${jobId}/download`, {
        responseType: 'blob',
      });
      return resp.data;
    }),

  // ---------------------------------------------------------------------------
  // Screening routes (HTTP entrypoints that enqueue worker jobs)
  // ---------------------------------------------------------------------------

  runDatabaseScreening: async (request: ProductDatabaseScreeningRequest) =>
    withProductService('runDatabaseScreening', async () => {
      const payload = normalizeDatabaseScreeningRequest(request);
      if (!payload.databaseIds) {
        throw new Error('No databaseId provided');
      }

      const resp = await (
        await getProductServiceClient()
      ).post('screenings/run/products', payload);
      return resp.data;
    }),

  runProductDatabaseScreening: async (
    request: ProductDatabaseScreeningRequest,
  ) =>
    withProductService('runProductDatabaseScreening', async () => {
      const payload = normalizeDatabaseScreeningRequest(request);
      if (!payload.databaseIds) {
        throw new Error('No databaseId provided');
      }

      const resp = await (
        await getProductServiceClient()
      ).post('screenings/run/products', payload);
      return resp.data;
    }),

  runSingleProductScreening: async (request: SingleProductScreeningRequest) =>
    withProductService('runSingleProductScreening', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(
        'screenings/run/product',
        normalizeSingleProductScreeningRequest(request),
      );
      return resp.data;
    }),

  runProductCheckSingle: async (request: SingleProductScreeningRequest) =>
    withProductService('runProductCheckSingle', async () => {
      const resp = await (
        await getProductServiceClient()
      ).post(
        'screenings/run/product',
        normalizeSingleProductScreeningRequest(request),
      );
      return resp.data;
    }),

  getDatabaseScreeningStatus: async (databaseId: DatabaseId) =>
    withProductService('getDatabaseScreeningStatus', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get('screenings/status', {
        params: { databaseId: String(databaseId) },
      });
      return resp.data;
    }),

  getScreeningProgress: async (jobId: string) =>
    withProductService('getScreeningProgress', async () => {
      const resp = await (
        await getProductServiceClient()
      ).get('screenings/progress', { params: { jobId: jobId } });
      return resp.data;
    }),
};

export default ProductServiceAPI;
